import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiSearch, FiXCircle, FiMail, FiFileText, FiClock } from 'react-icons/fi';
import axiosInstance from '../AxiosInstance';
import './SearchBar.css';

function SearchBar({ placeholder = "Search campaigns, templates, contacts...", onSearch, isMobile = false }) {
  const navigate = useNavigate();
  const [isFocused, setIsFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Fetch search results from unified API
  const fetchSearchResults = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        search: searchQuery.trim()
      });
      
      const response = await axiosInstance.get(`/campaigns/search/unified?${params}`);
      
      if (response?.data?.data) {
        const results = [];
        
        // Process campaigns
        if (response.data.data.campaigns && Array.isArray(response.data.data.campaigns)) {
          response.data.data.campaigns.forEach(campaign => {
            results.push({
              type: 'campaign',
              id: campaign.id,
              name: campaign.name,
              icon: <FiMail />,
              metadata: campaign.metadata
            });
          });
        }
        
        // Process templates
        if (response.data.data.templates && Array.isArray(response.data.data.templates)) {
          response.data.data.templates.forEach(template => {
            results.push({
              type: 'template',
              id: template.id,
              name: template.name,
              icon: <FiFileText />,
              metadata: template.metadata
            });
          });
        }
        
        // Process scheduled campaigns
        if (response.data.data.scheduledCampaigns && Array.isArray(response.data.data.scheduledCampaigns)) {
          response.data.data.scheduledCampaigns.forEach(scheduled => {
            results.push({
              type: 'scheduled',
              id: scheduled.id,
              name: scheduled.name,
              icon: <FiClock />,
              metadata: scheduled.metadata
            });
          });
        }
        
        // Use all results (no limit - scrollbar will handle overflow)
        setSuggestions(results);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching search results:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounce search API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        fetchSearchResults(searchTerm);
      } else {
        setSuggestions([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setShowSuggestions(true);
    setSelectedIndex(-1);
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (searchTerm.trim() && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleBlur = (e) => {
    // Delay to allow click on suggestion
    // Check if click is inside suggestions container
    if (suggestionsRef.current && suggestionsRef.current.contains(e.relatedTarget)) {
      return;
    }
    // Check if click is on a suggestion item
    if (e.relatedTarget && e.relatedTarget.closest('.search-suggestion-item')) {
      return;
    }
    setTimeout(() => {
      if (!searchContainerRef.current?.contains(document.activeElement)) {
        setIsFocused(false);
        setShowSuggestions(false);
      }
    }, 250);
  };

  const handleSearchSubmit = (suggestion = null) => {
    if (suggestion) {
      // Save search term before clearing
      const currentSearchTerm = searchTerm;
      
      // Navigate based on type
      if (suggestion.type === 'template') {
        // Use TemplateId from metadata if available, otherwise use id
        const templateId = suggestion.metadata?.TemplateId || suggestion.metadata?.templateId || suggestion.id;
        setSearchTerm('');
        setShowSuggestions(false);
        setIsFocused(false);
        navigate(`/modern-email-builder/${templateId}`);
      } else if (suggestion.type === 'campaign') {
        // Get campaign ID from metadata or id - prioritize campaignId
        const campaignId = suggestion.metadata?.campaignId || suggestion.id || suggestion.metadata?._id;
        setSearchTerm('');
        setShowSuggestions(false);
        setIsFocused(false);
        // Navigate with campaign ID in state to open details modal
        navigate('/email-campaigns', { 
          state: { 
            openCampaignId: campaignId,
            searchTerm: currentSearchTerm 
          } 
        });
        // Optionally pass search term to filter campaigns
        if (onSearch) {
          onSearch(currentSearchTerm);
        }
      } else if (suggestion.type === 'scheduled') {
        // Get scheduled campaign ID - prioritize campaignId
        const campaignId = suggestion.metadata?.campaignId || suggestion.id || suggestion.metadata?._id;
        setSearchTerm('');
        setShowSuggestions(false);
        setIsFocused(false);
        // Navigate with campaign ID in state to open details modal
        navigate('/email-campaigns', { 
          state: { 
            openCampaignId: campaignId,
            searchTerm: currentSearchTerm 
          } 
        });
        if (onSearch) {
          onSearch(currentSearchTerm);
        }
      }
    } else {
      // Full search - navigate to campaigns page with search
      if (searchTerm.trim()) {
        const term = searchTerm;
        setSearchTerm('');
        setShowSuggestions(false);
        setIsFocused(false);
        navigate('/email-campaigns');
        if (onSearch) {
          onSearch(term);
        }
      }
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSearchTerm('');
    setShowSuggestions(false);
    searchInputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSearchSubmit(suggestions[selectedIndex]);
      } else {
        handleSearchSubmit();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setIsFocused(false);
      searchInputRef.current?.blur();
    }
  };

  const displayPlaceholder = isMobile ? "Search..." : placeholder;
  
  // Show suggestions when focused and there are results or loading
  useEffect(() => {
    if (isFocused && (suggestions.length > 0 || loading || searchTerm.trim())) {
      setShowSuggestions(true);
    }
  }, [isFocused, suggestions.length, loading, searchTerm]);

  return (
    <div 
      ref={searchContainerRef}
      className={`search-bar-container ${isFocused ? 'focused' : ''} ${searchTerm ? 'has-value' : ''}`}
    >
      <FiSearch className="search-bar-icon" />
      <input
        ref={searchInputRef}
        type="text"
        placeholder={displayPlaceholder}
        className="search-bar-input"
        value={searchTerm}
        onChange={(e) => handleSearchChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {searchTerm && (
        <button 
          className="search-bar-clear"
          onClick={handleClear}
          aria-label="Clear search"
          type="button"
        >
          <FiXCircle />
        </button>
      )}
      
      {/* Search Suggestions Dropdown */}
      {showSuggestions && (suggestions.length > 0 || loading || searchTerm.trim()) && (
        <div className="search-bar-suggestions" ref={suggestionsRef}>
          <div className="search-suggestions-header">
            <span className="suggestions-title">
              {loading ? 'Searching...' : searchTerm ? 'Results' : 'Recent Searches'}
            </span>
          </div>
          {loading && (
            <div className="search-loading">
              <div className="loading-spinner"></div>
              <span>Searching...</span>
            </div>
          )}
          {!loading && suggestions.length > 0 && (
            <div className="search-suggestions-list">
              {suggestions.map((suggestion, index) => (
                <div
                  key={`${suggestion.type}-${suggestion.id}-${index}`}
                  className={`search-suggestion-item ${
                    index === selectedIndex ? 'selected' : ''
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSearchSubmit(suggestion);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent blur from firing before click
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="suggestion-icon">{suggestion.icon}</div>
                  <div className="suggestion-content">
                    <span className="suggestion-name">{suggestion.name}</span>
                    <span className="suggestion-type">
                      {suggestion.type === 'scheduled' 
                        ? 'Scheduled Campaign' 
                        : suggestion.type.charAt(0).toUpperCase() + suggestion.type.slice(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && searchTerm.trim() && suggestions.length === 0 && (
            <div className="search-no-results">
              <span>No results found for "{searchTerm}"</span>
            </div>
          )}
          {!loading && searchTerm && (
            <div className="search-suggestions-footer">
              <button 
                className="search-all-btn"
                onClick={() => handleSearchSubmit()}
                type="button"
              >
                <FiSearch />
                View all results for "{searchTerm}"
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchBar;

